import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Dialog, Input, type BadgeTone } from '@artosera/ui';
import {
  ajouterDomaine,
  ApiError,
  rendreDomainePrincipal,
  retirerDomaine,
  verifierDomaine,
} from '../../lib/api.js';
import { usePeut } from '../../auth/usePeut.js';
import { estElevationAnnulee, useElevation } from '../../components/useElevation.js';
import { formaterDate } from '../../lib/format.js';
import type { Domaine, EtatDomaine, GalerieFiche } from '../../types.js';
import pageStyles from '../../styles/page.module.css';
import { TableauDefilant } from '../../components/TableauDefilant.js';
import tableStyles from '../../styles/table.module.css';
import formStyles from '../../styles/form.module.css';

const TONE_DOMAINE: Record<EtatDomaine, BadgeTone> = {
  a_verifier: 'reserved',
  actif: 'available',
  desactive: 'deposit',
};

/** Les raisons d'échec que l'onglet sait dire ; les autres tombent sur `generique`. */
const RAISONS_CONNUES = new Set([
  'cname_absent',
  'cname_incorrect',
  'a_absent',
  'a_incorrect',
  'aaaa_present',
  'aaaa_incorrect',
  'aaaa_indetermine',
  'domaine_nu_non_configure',
  'domaine_non_actif',
  'suffixe_public',
  'domaine_deja_pris',
  'domaine_par_defaut',
  'hote_invalide',
  'sous_domaine_plateforme',
]);

/**
 * Un nom d'hôte tel que Caddy pourra le demander : le même motif que l'API
 * (`schemaDomaine`), pour dire l'erreur avant d'envoyer.
 */
const HOTE_VALIDE = /^(?=.{4,253}$)(?!-)[a-z0-9-]{1,63}(?<!-)(?:\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/;

/** Le domaine de la plateforme, lu sur le sous-domaine par défaut de la galerie (`<slug>.<suffixe>`). */
function suffixePlateforme(galerie: GalerieFiche): string {
  const defaut = galerie.domaines.find((domaine) => domaine.parDefaut);
  const prefixe = `${galerie.slug}.`;
  return defaut?.hote.startsWith(prefixe) ? defaut.hote.slice(prefixe.length) : 'artosera.gallery';
}

/** La raison pour laquelle un hôte saisi ne peut pas partir, ou `null` s'il peut. */
function refusLocal(
  hote: string,
  suffixe: string,
): 'hote_invalide' | 'sous_domaine_plateforme' | null {
  const nom = hote.trim().toLowerCase().replace(/\.$/, '');
  if (!HOTE_VALIDE.test(nom)) return 'hote_invalide';
  if (nom === suffixe || nom.endsWith(`.${suffixe}`)) return 'sous_domaine_plateforme';
  return null;
}

/** Une erreur de l'API, traduite : la raison (`details.raison`) et ce qui était attendu. */
function messageErreur(
  t: (cle: string, valeurs?: Record<string, string>) => string,
  hote: string,
  erreur: unknown,
): string {
  const details =
    erreur instanceof ApiError
      ? ((erreur.details ?? {}) as { raison?: unknown; attendu?: unknown; suffixe?: unknown })
      : {};
  const raison =
    typeof details.raison === 'string' && RAISONS_CONNUES.has(details.raison)
      ? details.raison
      : 'generique';
  const attendu = Array.isArray(details.attendu)
    ? details.attendu.filter((v): v is string => typeof v === 'string').join(', ')
    : typeof details.attendu === 'string'
      ? details.attendu
      : '';
  return t(`fiche.domaines.erreurs.${raison}`, {
    hote,
    attendu,
    suffixe: String(details.suffixe ?? ''),
  });
}

/** La consigne DNS d'un domaine : ce que la galerie pose chez son registraire. */
function Consigne({ domaine }: { domaine: Domaine }): JSX.Element {
  const { t } = useTranslation('console');
  if (domaine.parDefaut || domaine.dns === null) {
    return <span className={tableStyles.celluleMuette}>{t('fiche.domaines.servi')}</span>;
  }
  if (domaine.dns.type === 'CNAME') {
    return <code>{t('fiche.domaines.consigneCname', { valeur: domaine.dns.valeur })}</code>;
  }
  return (
    <span>
      <code>
        {domaine.dns.valeurs.length > 0
          ? t('fiche.domaines.consigneA', { valeurs: domaine.dns.valeurs.join(', ') })
          : t('fiche.domaines.consigneANonConfiguree')}
      </code>
      <br />
      <span className={tableStyles.celluleMuette}>
        {domaine.dns.valeursAaaa.length > 0
          ? t('fiche.domaines.consigneAaaa', { valeurs: domaine.dns.valeursAaaa.join(', ') })
          : t('fiche.domaines.sansAaaa')}
      </span>
    </span>
  );
}

export function DomainesTab({
  idGalerie,
  galerie,
}: {
  idGalerie: string;
  galerie: GalerieFiche;
}): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const peutGerer = peut('console:manage-domains');
  const queryClient = useQueryClient();
  const [ouvert, setOuvert] = useState(false);
  const [hote, setHote] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  // L'erreur d'ajout se lit DANS le dialogue : derrière son voile, personne ne la verrait.
  const [erreurAjout, setErreurAjout] = useState<string | null>(null);
  const [aRetirer, setARetirer] = useState<string | null>(null);
  // Retirer un domaine demande un second facteur récent.
  const elevation = useElevation();
  const suffixe = suffixePlateforme(galerie);

  const invalider = (): Promise<void> =>
    queryClient
      .invalidateQueries({ queryKey: ['console', 'galerie', idGalerie] })
      .then(() => undefined);

  const signaler = (cible: string) => (cause: unknown) => {
    setErreur(messageErreur(t, cible, cause));
  };

  const ajouter = useMutation({
    mutationFn: () => ajouterDomaine(idGalerie, hote.trim()),
    onMutate: () => setErreurAjout(null),
    onSuccess: async () => {
      setOuvert(false);
      setHote('');
      await invalider();
    },
    onError: (cause) => setErreurAjout(messageErreur(t, hote.trim(), cause)),
  });
  const tenterAjout = (): void => {
    const refus = refusLocal(hote, suffixe);
    if (refus !== null) {
      setErreurAjout(t(`fiche.domaines.erreurs.${refus}`, { hote: hote.trim(), suffixe }));
      return;
    }
    ajouter.mutate();
  };
  const verifier = useMutation({
    mutationFn: (cible: string) => verifierDomaine(idGalerie, cible),
    onMutate: () => setErreur(null),
    onSuccess: () => invalider(),
    onError: (cause, cible) => signaler(cible)(cause),
  });
  const principal = useMutation({
    mutationFn: (cible: string) => rendreDomainePrincipal(idGalerie, cible),
    onMutate: () => setErreur(null),
    onSuccess: () => invalider(),
    onError: (cause, cible) => signaler(cible)(cause),
  });
  const retirer = useMutation({
    mutationFn: (cible: string) => elevation.executer(() => retirerDomaine(idGalerie, cible)),
    onMutate: () => setErreur(null),
    onSuccess: async () => {
      setARetirer(null);
      await invalider();
    },
    onError: (cause, cible) => {
      setARetirer(null);
      if (!estElevationAnnulee(cause)) signaler(cible)(cause);
    },
  });

  return (
    <div>
      <div className={pageStyles.sectionTitre}>
        <h2>{t('fiche.domaines.titre')}</h2>
        {peutGerer ? (
          <Dialog
            open={ouvert}
            onOpenChange={(valeur) => {
              setOuvert(valeur);
              if (valeur) setErreurAjout(null);
            }}
            trigger={<Button size="sm">{t('fiche.domaines.ajouter')}</Button>}
            title={t('fiche.domaines.dialogueTitre')}
            description={t('fiche.domaines.dialogueTexte')}
            closeLabel={t('fiche.domaines.fermer')}
            footer={
              <>
                <Button variant="secondary" onClick={() => setOuvert(false)}>
                  {t('fiche.domaines.annuler')}
                </Button>
                <Button loading={ajouter.isPending} disabled={!hote.trim()} onClick={tenterAjout}>
                  {t('fiche.domaines.ajouter')}
                </Button>
              </>
            }
          >
            <div className={formStyles.champ}>
              <label htmlFor="domaine-hote">{t('fiche.domaines.champHote')}</label>
              <Input
                id="domaine-hote"
                placeholder={`www.${galerie.slug}.fr`}
                value={hote}
                onChange={(event) => {
                  setHote(event.target.value);
                  setErreurAjout(null);
                }}
                aria-invalid={erreurAjout !== null}
              />
              {erreurAjout ? (
                <p className={formStyles.erreur} role="alert">
                  {erreurAjout}
                </p>
              ) : null}
            </div>
          </Dialog>
        ) : null}
      </div>

      <p className={formStyles.aide}>{t('fiche.domaines.aidePrincipal')}</p>

      {erreur ? (
        <p className={formStyles.erreur} role="alert">
          {erreur}
        </p>
      ) : null}

      {galerie.domaines.length === 0 ? (
        <p className={pageStyles.videEtat}>{t('fiche.domaines.vide')}</p>
      ) : (
        <TableauDefilant>
          <table className={tableStyles.table}>
            <thead>
              <tr>
                <th scope="col">{t('fiche.domaines.hote')}</th>
                <th scope="col">{t('fiche.domaines.etat')}</th>
                <th scope="col">{t('fiche.domaines.dns')}</th>
                <th scope="col">{t('fiche.domaines.verifieLe')}</th>
                <th scope="col">{t('fiche.domaines.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {galerie.domaines.map((domaine) => (
                <tr key={domaine.hote}>
                  <td className={tableStyles.cellulePrincipale}>
                    {domaine.hote}{' '}
                    {domaine.principal ? (
                      <Badge tone="available">{t('fiche.domaines.principal')}</Badge>
                    ) : null}
                  </td>
                  <td>
                    <Badge tone={TONE_DOMAINE[domaine.etat]}>
                      {t(`fiche.domaines.etatValeur.${domaine.etat}`)}
                    </Badge>
                  </td>
                  <td>
                    <Consigne domaine={domaine} />
                  </td>
                  <td>{formaterDate(domaine.verifieLe, t('fiche.domaines.jamaisVerifie'))}</td>
                  <td className={tableStyles.celluleActions}>
                    {/* Un domaine désactivé est une trace : plus rien à vérifier ni à retirer. */}
                    {peutGerer && domaine.etat === 'a_verifier' ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={verifier.isPending && verifier.variables === domaine.hote}
                        onClick={() => verifier.mutate(domaine.hote)}
                      >
                        {t('fiche.domaines.verifier')}
                      </Button>
                    ) : null}
                    {peutGerer && domaine.etat === 'actif' && !domaine.principal ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={principal.isPending && principal.variables === domaine.hote}
                        onClick={() => principal.mutate(domaine.hote)}
                      >
                        {t('fiche.domaines.rendrePrincipal')}
                      </Button>
                    ) : null}
                    {peutGerer && !domaine.parDefaut && domaine.etat !== 'desactive' ? (
                      <Button
                        variant="danger"
                        size="sm"
                        loading={retirer.isPending && retirer.variables === domaine.hote}
                        onClick={() => setARetirer(domaine.hote)}
                      >
                        {t('fiche.domaines.retirer')}
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableauDefilant>
      )}

      <Dialog
        open={aRetirer !== null && !elevation.enElevation}
        onOpenChange={(ouvert) => {
          if (!ouvert && !retirer.isPending) setARetirer(null);
        }}
        title={t('fiche.domaines.retrait.titre')}
        description={aRetirer ? t('fiche.domaines.retrait.texte', { hote: aRetirer }) : undefined}
        closeLabel={t('fiche.domaines.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setARetirer(null)} disabled={retirer.isPending}>
              {t('fiche.domaines.retrait.garder')}
            </Button>
            <Button
              variant="danger"
              loading={retirer.isPending}
              onClick={() => aRetirer && retirer.mutate(aRetirer)}
            >
              {retirer.isPending
                ? t('fiche.domaines.retrait.enCours')
                : t('fiche.domaines.retrait.confirmer')}
            </Button>
          </>
        }
      />
      {elevation.dialogue}
    </div>
  );
}
